import React from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';

import DraftJS, {EditorState, Modifier, RichUtils, SelectionState} from 'draft-js';
import {ContentState, KeyBindingUtil } from "draft-js";
import {DraftailEditor, DraftUtils} from "draftail"
import {
    INLINE_CONTROL,
    BLOCK_CONTROL,
    ENTITY_CONTROL
}  from "./toolbar-const";
import Toolbar from './components/toolbar/toolbar';
import { positionSuggestions, addMention, replaceText, addText } from './utils/utils'

const { hasCommandModifier } = KeyBindingUtil;
import SettingsButton from "./settings-button/settings-button";

import { AllHtmlEntities } from 'html-entities';

import LocalSettings from 'gd-local-settings';

import { composeDecorators } from 'draft-js-plugins-editor';
import createMentionPlugin from 'lib/draft-js-mention-plugin';
import createImagePlugin from './plugins/image/image-plugin';
import createAttachmentPlugin, { addAttachment } from './plugins/attachment/attachment-plugin';
import createBlockDndPlugin from './plugins/block-dnd/block-dnd-plugin';
import createHorizontalRulePlugin from './plugins/horizontal-rule-plugin/horizontal-rule-plugin';
import createResizeablePlugin from './plugins/resizeable/resizeable-plugin';
import createLinkifyPlugin from 'draft-js-linkify-plugin';
import createPastePlugin from './plugins/paste/paste-plugin';

import MentionsSuggestionsEntry from './mentions/suggestions/mentions-suggestions-entry';
import MentionComponent from './mentions/suggestions/mention-component';

import MentionsOptionsEntry from './mentions/options/mentions-options-entry';
import MentionOptionComponent from '../editor-universal/common/mention-option-component/mention-option-component';
import MentionOptionViewComponent from './mentions/options/option-component/mention-option-view-component';
import { ENTITY_TYPE } from "./api/constants";
import LinkComponent from "./renderers/link-component"
import * as RichEditorUtils from "./utils/utils";

export default class RichEditor extends React.Component {

    static propTypes = {
        onChange: PropTypes.func,
        stateSaveInterval: PropTypes.number,

        attachmentsCollection       : PropTypes.any,    // used by attachment plugin
        onAddAttachment             : PropTypes.func,   // if passed -> will handle select file for attachment and show attachment icon
        addField                    : PropTypes.object,   // used to add "Add field button" used in editor/formulas, see addFieldConfig object

        controlsParams              : PropTypes.shape({
            leftChildren    : PropTypes.object,
            rightChildren   : PropTypes.object
        }),                                                // usd to add buttons to toolbar if it is enabled (showControls)

        type: PropTypes.oneOf(['full', 'view']),
        forceComponentsOfViewType: PropTypes.bool, //currently this is only for mentions in note view editor to be in view state (clickable)
        text: PropTypes.string,
        rtfState: PropTypes.object,
        localSettingsName: PropTypes.string,
        useLocalSettings: PropTypes.bool,
        showControls: PropTypes.bool,
        showUndoRedo: PropTypes.bool,
        showSettingsButton: PropTypes.bool,
        hideControlsAllowed: PropTypes.bool,
        projectId: PropTypes.string, //for mention options initial project
        mentions: PropTypes.array,
        placeholder: PropTypes.string,
        onCommandEnterKey: PropTypes.func,
        onKeyDown: PropTypes.func,
        onPasteImage: PropTypes.func,
        onEscape: PropTypes.func,
        editorType: PropTypes.oneOf(['default','chat']),
    };

    static defaultProps = {
        type: 'view',
        text: null,
        rtfState: null,
        localSettingsName: "rich-editor.controls",
        useLocalSettings: true,
        showControls: true,
        showUndoRedo: true,
        showSettingsButton: true,
        hideControlsAllowed: true,
        mentions: [],
        forceComponentsOfViewType: false,
        editorType: 'default'
    };

    constructor(props) {
        super(props);

        this.htmlEntities = new AllHtmlEntities();

        this.localSettings = new LocalSettings(props.localSettingsName,{
            showControls: props.showControls ? '1' : '0'
        });
        const settingShowControls = this.localSettings.get("showControls") === '1';
        const showControls = props.useLocalSettings ? settingShowControls : props.showControls;

        this.inlineOptions = [
            {id: gd.const.editorLink.type.TASK, label: 'Link a task',      mentionId: 'linkTask'},
            {id: gd.const.editorLink.type.NOTE, label: 'Link a note',      mentionId: 'linkNote'},
            {id: gd.const.editorLink.type.USER, label: 'User mention'},
            {id: gd.const.editorLink.type.LINK, label: 'Insert web link'}
        ];

        this.state = {
            clearKey: this._generateKey(),
            rawContent: this.getDefaultContent(props),
            showControls: showControls,
            suggestions: this._addCommonOptionToSuggestions(props.mentions),
            options: this.inlineOptions,
        };

        this._preparePlugins(props);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {

        const content = this.getDefaultContent(nextProps);
        const newState = {};
        if (!_.isEqual(this.state.rawContent, content)) newState.rawContent = content;
        const mentions = this._addCommonOptionToSuggestions(nextProps.mentions);
        if (!_.isEqual(this.state.suggestions, mentions)) newState.suggestions = mentions;

        this._updateMentionComponent(nextProps);

        this.setState(newState)
    }

    componentDidMount() {
        this._setEditorAccessible();
    }

    componentDidUpdate() {
        this._setEditorAccessible();
    }

    _addCommonOptionToSuggestions = (suggestions) => {
        return [].concat(suggestions).concat({id:'all_commands', name:'All commands', type:gd.const.editorLink.type.SWITCH_OPTIONS})
    }

    getPrintContainer() {
        return findDOMNode(this.editor);
    }

    _updateMentionComponent = (props) => {
        this._mentionPlugin2.setMentionComponent(props.type === "view" || props.forceComponentsOfViewType ? MentionOptionViewComponent : MentionOptionComponent);
    }

    _preparePlugins(props) {
        this._mentionPlugin = createMentionPlugin({
            theme: {
                mentionSuggestions: "mentions-suggestions"
            },
            positionSuggestions: positionSuggestions.bind(this,'custom-popover-target'),
            entityMutability: "IMMUTABLE",
            mentionPrefix: "@",
            mentionTrigger: "@",
            mentionComponent: MentionComponent
        });
        this._mentionPlugin.onEscape = ()=>{props.onEscape ? props.onEscape() : null};

        this._mentionPlugin2 = createMentionPlugin({
            theme: {
                mentionSuggestions: "mentions-options"
            },
            positionSuggestions: positionSuggestions.bind(this,'custom-popover-target'),
            entityMutability: "IMMUTABLE",
            mentionPrefix: "/",
            mentionTrigger: "/",
            mentionEntityTypes: ['linkTask','linkNote']
        });
        this._updateMentionComponent(props);

        this._mentionPlugin2.onEscape = ()=>{props.onEscape ? props.onEscape() : null};

        this._resizeablePlugin = createResizeablePlugin({});
        this._blockDndPlugin = createBlockDndPlugin();
        this._horizontalRulePlugin = createHorizontalRulePlugin();
        this._linkifyPlugin = createLinkifyPlugin({component: LinkComponent});

        const decorator = composeDecorators(
            this._blockDndPlugin.decorator,
            this._resizeablePlugin.decorator
        );
        this._imagePlugin       = createImagePlugin({ decorator });

        //
        this._attachmentPlugin  = createAttachmentPlugin({ decorator }, this.props.attachmentsCollection);


        this._pastePlugin       = createPastePlugin();
    }

    _setEditorAccessible = () => {this.props.type === 'full' ? this.editor.unlockEditor() : this.editor.lockEditor()}

    clear = () => {
        this.setState({
            clearKey: this._generateKey(),
            rawContent: null,
        })
    }

    //is required for editor to update
    _generateKey = () => ""+gd.utils.randomInt(100000, 1)

    getDefaultContent = (props) => {
        const { text, rtfState }= props;

        let res;

        if (rtfState && rtfState.blocks) {
            res = rtfState;
        } else if (text) {
            const message = this.htmlEntities.decode(text);
            res = DraftJS.convertToRaw(ContentState.createFromText(message));
        } else {
            res = null; //DraftJS.convertToRaw(ContentState.createFromText(''));
        }

        return res;
    }

    onSave = (content) => {
        const { onChange } = this.props;
        const { rawContent } = this.state;
        if (!_.isEqual(rawContent,content)) {
            this.setState({
                rawContent: content
            }, ()=>{
                if (onChange) {

                    // old
                    // const contentText = content && content.blocks ? content.blocks.reduce((text, block)=>(text ? text + ' ' : '') + block.text, '') : '';

                    // THIS IS NEW by VIC Nov 15 2022
                    // oi pipec kokechno..... no nadus' ok budet
                    // new with new line
                    // const contentText = RichEditorUtils.contentToTextWithoutNewLine(content)
                    const contentText = RichEditorUtils.contentToTextWithNewLine(content)

                    onChange(contentText, content);
                }
            });
        }
    }

    onSearchChange = (value, callback) => {
        const { mentions } = this.props;
        const searchValue = value.toLowerCase();
        const suggestions = this._addCommonOptionToSuggestions(mentions.filter(mentions=>!searchValue || mentions.name.toLowerCase().indexOf(searchValue) > -1));
        if (callback) callback(suggestions);

        this.setState({
            suggestions: suggestions
        });
    };

    onOptionSearchChange = (value, callback) => {
        const searchValue = value.toLowerCase();
        const options = _.filter(this.inlineOptions, option=>!searchValue || option.label.toLowerCase().indexOf(searchValue) > -1);
        if (callback) callback(options);

        this.setState({
            options: options,
        });
    };

    focus = () => {this.editor.focus();}
    blur = () => {/*this.editor.blur();*/}

    onPaste = (e) => {
        // We need to check if event.clipboardData is supported (Chrome & IE)
        if (e.clipboardData && e.clipboardData.files) {

            // Get the items from the clipboard
            const files = e.clipboardData.files;
            const len = files.length;

            for (let i = 0; i < len; i++) {
                const file = files[i];
                if (file.type.indexOf('image') !== -1) {
                    if (this.props.onPasteImage) this.props.onPasteImage(file);

                    // Prevent the image (or URL) from being pasted into the contenteditable element
                    e.preventDefault();
                }
            }
        }
    }

    onEditorKeyDown = (e) => {
        const { onCommandEnterKey } = this.props;
        if (onCommandEnterKey && e.keyCode === 13 && hasCommandModifier(e)) {
            e.preventDefault();
            e.stopPropagation();
            onCommandEnterKey();
        } else {
            const { onKeyDown } = this.props;
            if (onKeyDown) onKeyDown(e);
        }
    }

    onChangeToolbarState = (value) => {
        this.localSettings.set("showControls", value ? '1' : '0');
        this.setState({
            showControls: value
        });
    }

    handleUserMentionSelect = (mention, callback) => {
        if (mention.type === gd.const.editorLink.type.SWITCH_OPTIONS) this._replaceText('@','/');
        else if (callback) callback()
    }

    _replaceText = (textToReplace = '/', newText = '') => {
        let editorState = this.editor.getEditorState();
        const newEditorState = replaceText(editorState, textToReplace, newText);
        this.editor.onChange(newEditorState);
    };

    handleRemoveMentionTrigger = (mentionTrigger) => {
        this._replaceText(mentionTrigger,'');
    }

    handleOptionSelect = (option) => {

        const resultCallback = (result)=>{
            let editorState = this.editor.getEditorState();
            this.editor.onChange(addMention(editorState, result, '/', '/'));
        };

        switch (option.id) {
            case gd.const.editorLink.type.TASK: gd.modals.mentions.taskMention(gd.companyId, this.props.projectId, resultCallback); break;
            case gd.const.editorLink.type.NOTE: gd.modals.mentions.noteMention(gd.companyId, this.props.projectId, resultCallback); break;
            case gd.const.editorLink.type.USER: this._replaceText('/','@'); break;

            case gd.const.editorLink.type.LINK:
                this._replaceText('/','');
                const entityKey = DraftUtils.getSelectionEntity(this.editor.getEditorState());
                this.editor.toggleSource(ENTITY_TYPE.LINK, entityKey, entityKey ? contentState.getEntity(entityKey) : null);
                break;
        }
    }

    onAddMention = () => {
        setTimeout(()=>{this.focus()}, 0);
    }

    onLinkTask = () => {
        this.handleOptionSelect({id:gd.const.editorLink.type.TASK});
    }

    onLinkNote = () => {
        this.handleOptionSelect({id:gd.const.editorLink.type.NOTE});
    }

    addAttachmentToEditor = (attachment) => {
        const newEditorState = addAttachment(this.editor.getEditorState(), attachment);
        this.editor.onChange(newEditorState);
    }

    addAttachmentsToEditor = (attachments) => {
        let newEditorState = this.editor.getEditorState()
        _.map(attachments, attachment=> {
            newEditorState = addAttachment(newEditorState, attachment)
        })

        this.editor.onChange(newEditorState);
    }

    addTextToEditor = (text, postfix = '') => {
        let newEditorState = this.editor.getEditorState()
        newEditorState = addText(newEditorState, text + postfix)

        this.editor.onChange(newEditorState)
    }

    addLinkToEditor = (label, url, postfix = '') => {
        const editorState = this.editor.getEditorState()
        let contentState = editorState.getCurrentContent()

        let selection = editorState.getSelection()
        const data = {url: url.replace(/\s/g, "")}

        let nextEditorState = editorState;
        if (selection.anchorOffset === selection.focusOffset) {
            contentState = Modifier.insertText(contentState, selection, label)
            nextEditorState = EditorState.push(nextEditorState, contentState, 'insert-characters')

            selection = new SelectionState({
                anchorKey   : selection.getAnchorKey(),
                anchorOffset: selection.anchorOffset,
                focusKey    : selection.getAnchorKey(),
                focusOffset : selection.anchorOffset + label.length,
            });
        }

        const contentStateWithEntity = contentState.createEntity(ENTITY_TYPE.LINK, "MUTABLE", data)
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey()

        nextEditorState = RichUtils.toggleLink(
            nextEditorState,
            selection,
            entityKey,
        )

        //also focuses cursor at the end of the editor
        nextEditorState = EditorState.moveSelectionToEnd(nextEditorState)
        nextEditorState = addText(nextEditorState, postfix)
        nextEditorState = EditorState.moveSelectionToEnd(nextEditorState)

        this.editor.onChange(nextEditorState)
    }

    render() {
        let classes = Classes('rte-editor', {
            'with-controls': this.state.showControls
        });

        const { MentionSuggestions } = this._mentionPlugin;
        const { MentionSuggestions:MentionOptions } = this._mentionPlugin2;
        const plugins = [
            this._blockDndPlugin,
            this._horizontalRulePlugin,
            this._mentionPlugin,
            this._mentionPlugin2,
            this._resizeablePlugin,
            this._imagePlugin,
            this._attachmentPlugin,
            this._linkifyPlugin,
            this._pastePlugin
        ];

        const entityTypes = [ENTITY_CONTROL.LINK, ENTITY_CONTROL.IMAGE, ENTITY_CONTROL.ATTACHMENT];
        const entityTypesReadOnly = [ENTITY_CONTROL.LINK_READ_ONLY, ENTITY_CONTROL.IMAGE, ENTITY_CONTROL.ATTACHMENT];
        let blockTypes, inlineStyles;

        switch (this.props.editorType) {
            case 'chat':
                //no block types and inline styles
                break;

            default:
                blockTypes = Object.values(BLOCK_CONTROL);
                inlineStyles = Object.values(INLINE_CONTROL);
                break;
        }

        switch(this.props.type){
            case 'full':

                const toolbarFull = this.state.showControls
                    ? props=><Toolbar {...props}
                                      controlsParams    = {this.props.controlsParams}
                                      addField          = {this.props.addField}
                                      onAddAttachment   = {this.props.onAddAttachment}
                                      showUndoRedo      = {this.props.showUndoRedo}
                                      onLinkTask        = {this.onLinkTask} onLinkNote={this.onLinkNote} onAddMention={this.onAddMention}/>
                    : null

                return (
                    <div className={Classes("rte-root", this.props.className)}>
                        {this.props.showSettingsButton &&
                            <SettingsButton showToolbar             = {this.state.showControls}
                                            hideToolbarAllowed      = {this.props.hideControlsAllowed}
                                            onChangeToolbarState    = {this.onChangeToolbarState}/>}

                        <div className={classes} onClick={this.focus} onPaste={this.onPaste} onKeyDown={this.onEditorKeyDown}>
                            <DraftailEditor key={this.state.clearKey}
                                            ref={r=>this.editor = r}
                                            plugins={plugins}
                                            placeholder={this.props.placeholder}
                                            rawContentState={this.state.rawContent}
                                            onSave={this.onSave}
                                            stateSaveInterval={this.props.stateSaveInterval}
                                            topToolbar={toolbarFull}
                                            stripPastedStyles={true}
                                            // enableHorizontalRule={{description: "Horizontal rule"}}
                                            maxListNesting={4}
                                            blockTypes={blockTypes}
                                            inlineStyles={inlineStyles}
                                            entityTypes={entityTypes}/>
                            <MentionSuggestions onSearchChange={this.onSearchChange}
                                                suggestions={this.state.suggestions}
                                                entryComponent={MentionsSuggestionsEntry}
                                                onSelectOption={this.handleUserMentionSelect}
                                                onRemoveMentionTrigger={this.handleRemoveMentionTrigger}/>

                            <MentionOptions onSearchChange={this.onOptionSearchChange}
                                            suggestions={this.state.options}
                                            entryComponent={MentionsOptionsEntry}
                                            onSelectOption={this.handleOptionSelect}
                                            onRemoveMentionTrigger={this.handleRemoveMentionTrigger}/>
                        </div>
                        {this.props.children}
                        <div className="cf"/>
                    </div>
                );

            case 'view':
                return (
                    <div className={Classes("rte-root", this.props.className)}>
                        <DraftailEditor key={this._generateKey()}
                                        ref={r=>this.editor = r}
                                        readOnly={true}
                                        plugins={plugins}
                                        placeholder={this.props.placeholder}
                                        rawContentState={this.state.rawContent}
                                        topToolbar={null}
                                        maxListNesting={6}
                                        blockTypes={blockTypes}
                                        inlineStyles={inlineStyles}
                                        entityTypes={entityTypesReadOnly}/>
                        {this.props.children}
                        <div className="cf"/>
                    </div>
                );
        }
    }
}